import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, Chip, IconButton, Fade, Grow } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, CheckCircle as CheckIcon } from '@mui/icons-material';

interface EmailListEditorProps {
    value: string;
    onChange: (value: string) => void;
    sx?: any;
    helperText?: string;
    error?: boolean;
    errorMessage?: string;
    missingField?: boolean;
    label?: string;
    theme?: 'admin' | 'onboarder';
}

const EmailListEditor: React.FC<EmailListEditorProps> = ({
    value,
    onChange,
    sx,
    helperText,
    error,
    errorMessage,
    missingField = false,
    label = 'Contact Email Addresses',
    theme = 'onboarder'
}) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isValidInput, setIsValidInput] = useState(false);
    const [showAddButton, setShowAddButton] = useState(false);

    // Convert comma-separated string to array on mount and when value changes
    useEffect(() => {
        if (value) {
            const emailArray = value.split(',').map(email => email.trim()).filter(email => email.length > 0);
            setEmails(emailArray);
        } else {
            setEmails([]);
        }
    }, [value]);

    // Email validation
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Check if current input is valid and update visual state
    useEffect(() => {
        const trimmedEmail = newEmail.trim().toLowerCase();
        const isValid = trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail);
        setIsValidInput(isValid);
        setShowAddButton(trimmedEmail.length > 0);

        // Clear error when typing
        if (emailError && trimmedEmail) {
            setEmailError('');
        }
    }, [newEmail, emails, emailError]);

    // Add new email
    const addEmail = (emailToAdd: string) => {
        const trimmedEmail = emailToAdd.trim().toLowerCase();

        if (!trimmedEmail) return;

        if (!isValidEmail(trimmedEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        if (emails.includes(trimmedEmail)) {
            setEmailError('This email is already added');
            return;
        }

        const updatedEmails = [...emails, trimmedEmail];
        setEmails(updatedEmails);
        setNewEmail('');
        setEmailError('');

        // Convert back to comma-separated string
        onChange(updatedEmails.join(','));
    };

    // Handle manual add button click
    const handleAddEmail = () => {
        addEmail(newEmail);
    };

    // Remove email
    const handleRemoveEmail = (emailToRemove: string) => {
        const updatedEmails = emails.filter(email => email !== emailToRemove);
        setEmails(updatedEmails);

        // Convert back to comma-separated string
        onChange(updatedEmails.join(','));
    };

    // Handle input changes with auto-add on separators
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Check for separators (comma, semicolon, space, tab)
        const separators = [',', ';', ' ', '\t'];
        const hasSeparator = separators.some(sep => value.includes(sep));

        if (hasSeparator) {
            // Extract email before separator
            let emailPart = '';
            for (const sep of separators) {
                if (value.includes(sep)) {
                    emailPart = value.split(sep)[0];
                    break;
                }
            }

            if (emailPart.trim()) {
                addEmail(emailPart);
            } else {
                setNewEmail('');
            }
        } else {
            setNewEmail(value);
        }
    };

    // Handle key press events
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail(newEmail);
        } else if (e.key === 'Backspace' && !newEmail && emails.length > 0) {
            // Delete last email if backspace on empty input
            handleRemoveEmail(emails[emails.length - 1]);
        }
    };

    // Handle paste events to auto-parse multiple emails
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const potentialEmails = pastedText.split(/[,;\s\n\t]+/).filter(email => email.trim());

        potentialEmails.forEach(email => {
            if (email.trim()) {
                addEmail(email);
            }
        });
    };

    // Theme-specific styles
    const getFieldSx = () => {
        const baseStyles = {
            '& .MuiInputBase-input': {
                fontSize: '14px',
            },
        };

        if (theme === 'admin') {
            return {
                ...baseStyles,
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.5)',
                        borderWidth: isValidInput ? '2px' : '1px',
                        transition: 'all 0.2s ease'
                    },
                    '&:hover fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : 'rgba(255, 255, 255, 0.7)'
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : '#ffffff',
                        borderWidth: '2px'
                    },
                },
                '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ffffff',
                },
                '& .MuiInputBase-input': {
                    ...baseStyles['& .MuiInputBase-input'],
                    color: '#ffffff',
                },
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
            };
        }

        // Onboarder theme styles
        const onboarderStyles = missingField ? {
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(224, 177, 241, 0.15)',
                '& fieldset': {
                    borderColor: isValidInput ? 'rgba(76, 175, 80, 0.8)' : 'rgba(224, 177, 241, 0.7)',
                    borderWidth: isValidInput ? '2px' : '2px',
                    transition: 'all 0.2s ease'
                },
                '&:hover': {
                    backgroundColor: 'rgba(224, 177, 241, 0.2)',
                    '& fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : 'rgba(224, 177, 241, 0.9)',
                    }
                },
                '&.Mui-focused': {
                    backgroundColor: 'rgba(224, 177, 241, 0.2)',
                    '& fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : 'rgba(224, 177, 241, 1)',
                        borderWidth: '2px'
                    }
                }
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(224, 177, 241, 0.9)',
                fontWeight: 'bold',
            },
            '& .MuiInputBase-input': {
                ...baseStyles['& .MuiInputBase-input'],
                color: 'rgba(255, 255, 255, 0.9)',
            }
        } : {
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '& fieldset': {
                    borderColor: isValidInput ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.3)',
                    borderWidth: isValidInput ? '2px' : '1px',
                    transition: 'all 0.2s ease'
                },
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : 'rgba(255, 255, 255, 0.5)',
                    }
                },
                '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& fieldset': {
                        borderColor: isValidInput ? 'rgba(76, 175, 80, 1)' : '#d1c4e9',
                        borderWidth: '2px'
                    }
                }
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputBase-input': {
                ...baseStyles['& .MuiInputBase-input'],
                color: 'rgba(255, 255, 255, 0.9)',
            }
        };

        return {
            ...baseStyles,
            ...onboarderStyles,
            borderRadius: '8px',
            transition: 'all 0.2s ease'
        };
    };

    const getIconButtonSx = () => {
        const baseStyles = {
            transition: 'all 0.2s ease',
            transform: isValidInput ? 'scale(1.05)' : 'scale(1)',
        };

        if (theme === 'admin') {
            return {
                ...baseStyles,
                backgroundColor: isValidInput ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                '&:hover': {
                    backgroundColor: isValidInput ? 'rgba(76, 175, 80, 1)' : 'rgba(255, 255, 255, 0.25)',
                },
                mt: 0.5,
                opacity: showAddButton ? 1 : 0.3,
            };
        }

        return {
            ...baseStyles,
            backgroundColor: isValidInput
                ? 'rgba(76, 175, 80, 0.8)'
                : missingField
                    ? 'rgba(224, 177, 241, 0.2)'
                    : 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            '&:hover': {
                backgroundColor: isValidInput
                    ? 'rgba(76, 175, 80, 1)'
                    : missingField
                        ? 'rgba(224, 177, 241, 0.3)'
                        : 'rgba(255, 255, 255, 0.25)',
            },
            mt: 0.5,
            opacity: showAddButton ? 1 : 0.3,
        };
    };

    const getChipSx = () => {
        if (theme === 'admin') {
            return {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                fontSize: '13px',
                height: '28px',
                '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    '&:hover': {
                        color: 'white'
                    }
                },
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-1px)'
                }
            };
        }

        return {
            backgroundColor: missingField ? 'rgba(224, 177, 241, 0.2)' : 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            fontSize: '13px',
            height: '28px',
            '& .MuiChip-deleteIcon': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '16px',
                '&:hover': {
                    color: 'white'
                }
            },
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: missingField ? 'rgba(224, 177, 241, 0.3)' : 'rgba(255, 255, 255, 0.25)',
                transform: 'translateY(-1px)'
            }
        };
    };

    const getLabelColor = () => {
        if (theme === 'admin') {
            return 'rgba(255, 255, 255, 0.7)';
        }
        return missingField ? 'rgba(224, 177, 241, 0.9)' : 'rgba(255, 255, 255, 0.7)';
    };

    const getHelperTextColor = () => {
        if (theme === 'admin') {
            return emailError ? 'error.main' : 'rgba(255, 255, 255, 0.7)';
        }
        return emailError ? 'error.main' : missingField ? '#C0C0DD' : 'lightgray';
    };

    const getPlaceholderText = () => {
        if (emails.length === 0) {
            return "Enter email addresses • Press Enter, Space, or Comma to add";
        }
        return "Add another email address...";
    };

    const getHelperTextContent = () => {
        if (emailError) return emailError;
        if (helperText) return helperText;
        if (emails.length === 0) {
            return "💡 Tip: You can paste multiple emails at once or separate them with commas";
        }
        return `${emails.length} email${emails.length !== 1 ? 's' : ''} added`;
    };

    return (
        <Box sx={sx}>
            <Typography variant="body2" sx={{
                color: getLabelColor(),
                mb: 1,
                fontWeight: missingField ? 'bold' : 'normal'
            }}>
                {label} {missingField && '(Required)'}
            </Typography>

            {/* Display existing emails as chips */}
            {emails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {emails.map((email, index) => (
                        <Grow
                            key={email}
                            in={true}
                            style={{ transformOrigin: '0 0 0' }}
                            timeout={200 + index * 100}
                        >
                            <Chip
                                label={email}
                                onDelete={() => handleRemoveEmail(email)}
                                deleteIcon={<DeleteIcon />}
                                sx={getChipSx()}
                            />
                        </Grow>
                    ))}
                </Box>
            )}

            {/* Input for new email */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    fullWidth
                    value={newEmail}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onPaste={handlePaste}
                    placeholder={getPlaceholderText()}
                    error={!!emailError}
                    helperText={getHelperTextContent()}
                    FormHelperTextProps={{
                        sx: {
                            color: getHelperTextColor(),
                            fontWeight: missingField ? 'medium' : 'normal',
                            fontSize: '12px'
                        }
                    }}
                    sx={getFieldSx()}
                    InputProps={{
                        endAdornment: isValidInput && (
                            <Fade in={true}>
                                <CheckIcon sx={{
                                    color: 'rgba(76, 175, 80, 0.8)',
                                    mr: 1,
                                    fontSize: '20px'
                                }} />
                            </Fade>
                        )
                    }}
                />
                <Fade in={showAddButton}>
                    <IconButton
                        onClick={handleAddEmail}
                        disabled={!isValidInput}
                        sx={getIconButtonSx()}
                    >
                        <AddIcon />
                    </IconButton>
                </Fade>
            </Box>

            {error && errorMessage && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default EmailListEditor; 